import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import Select from 'react-select';
import { IoAddOutline } from 'react-icons/io5';
import { Field, Group } from '../../common/index';
import { useConfirmationModalContext } from '../../context/ConfirmationModalContext';
import { POSTS_FOR_ZONE } from '../../queries/post';
import {
  GET_BOOKLETS_WITH_SERIES,
  UPDATE_BOOKLET_SERIES,
  ADD_POST_TO_SERIES,
  REMOVE_POST_FROM_SERIES,
  REORDER_SERIES_POSTS,
} from '../../queries/bookletSeries';
import PostFormModal from './PostFormModal';
import ReorderablePostList from './ReorderablePostList';

function SeriesPostsForm({ series, zoneId, onComplete }) {
  const confirmationModalContext = useConfirmationModalContext();

  const refetchQueries = [{ query: GET_BOOKLETS_WITH_SERIES, variables: { zoneId } }];
  const onError = (err) => confirmationModalContext.showConfirmation('Fel', err.message);

  const [header, setHeader] = useState({
    postsTitle: series.postsTitle || '',
    postsButtonLabel: series.postsButtonLabel || '',
    postsButtonLink: series.postsButtonLink || '',
  });
  const onHeaderChange = (patch) => setHeader((prev) => ({ ...prev, ...patch }));

  const [creatingPost, setCreatingPost] = useState(false);

  const { data: postsData } = useQuery(POSTS_FOR_ZONE, { variables: { zoneId } });

  const [updateSeries, { loading: savingHeader }] = useMutation(UPDATE_BOOKLET_SERIES, { refetchQueries, onError });
  const [addPostToSeries] = useMutation(ADD_POST_TO_SERIES, { refetchQueries, onError });
  const [removePostFromSeries] = useMutation(REMOVE_POST_FROM_SERIES, { refetchQueries, onError });
  const [reorderSeriesPosts] = useMutation(REORDER_SERIES_POSTS, { refetchQueries, onError });

  const attachedIds = useMemo(
    () => new Set((series.postIds || []).map(String)),
    [series.postIds]
  );

  // Order by postIds (the ordering source of truth) so it reflects regardless of the order the
  // backend returns `posts` in.
  const attachedPosts = useMemo(() => {
    const byId = {};
    (series.posts || []).forEach((p) => { byId[String(p._id)] = p; });
    return (series.postIds || []).map((id) => byId[String(id)]).filter(Boolean);
  }, [series.posts, series.postIds]);

  // Pick from every post in the zone that isn't already attached.
  const availableOptions = useMemo(
    () =>
      (postsData?.postsForZone || [])
        .filter((p) => !attachedIds.has(String(p._id)))
        .map((p) => ({ value: p._id, label: p.title || '(ingen titel)' })),
    [postsData, attachedIds]
  );

  const attach = (postId) => postId && addPostToSeries({ variables: { seriesId: series._id, postId } });
  const detach = (postId) => removePostFromSeries({ variables: { seriesId: series._id, postId } });

  const handleReorder = (postIds) => {
    reorderSeriesPosts({ variables: { seriesId: series._id, postIds } });
  };

  const saveHeader = (e) => {
    e.preventDefault();
    // onError resolves (not rejects) the promise, so only close when the save actually succeeded.
    updateSeries({ variables: { _id: series._id, ...header } }).then((res) => {
      if (res?.data) onComplete();
    });
  };

  // PostsForm → Form calls onComplete(result); grab the new id and attach it to this series.
  const handleCreated = (result) => {
    setCreatingPost(false);
    const newId = result?.data?.createPost?._id;
    if (newId) attach(newId);
  };

  return (
    <>
      <form onSubmit={saveHeader}>
        <div className="form-content">
          <Group title="Rubrik">
            <Field type="text" name="postsTitle" title="Titel" value={header.postsTitle} onChange={onHeaderChange} />
            <Field type="text" name="postsButtonLabel" title="Knapptext" value={header.postsButtonLabel} onChange={onHeaderChange} />
            <Field type="text" name="postsButtonLink" title="Knapplänk (URL)" value={header.postsButtonLink} onChange={onHeaderChange} />
          </Group>

          <section className="series-posts-section">
            <h3 className="series-posts-heading">Inlägg i serien</h3>
            <ReorderablePostList posts={attachedPosts} onReorder={handleReorder} onRemove={detach} />
          </section>

          <section className="series-posts-section">
            <h3 className="series-posts-heading">Lägg till inlägg</h3>
            <div className="series-posts-add">
              <Select
                className="series-posts-picker"
                classNamePrefix="series-posts-picker"
                options={availableOptions}
                value={null}
                placeholder="Välj befintligt inlägg…"
                noOptionsMessage={() => 'Inga fler inlägg i zonen'}
                onChange={(opt) => attach(opt?.value)}
              />
              <button type="button" className="button secondary" onClick={() => setCreatingPost(true)}>
                <IoAddOutline size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Skapa nytt inlägg
              </button>
            </div>
          </section>
        </div>

        <div className="form-buttons">
          <button type="button" className="secondary" onClick={() => onComplete()}>Stäng</button>
          <button type="submit" className="primary">{savingHeader ? 'Sparar…' : 'Spara'}</button>
        </div>
      </form>

      {creatingPost && createPortal(
        <PostFormModal zoneId={zoneId} onComplete={handleCreated} onClose={() => setCreatingPost(false)} />,
        document.querySelector('.App') || document.body
      )}
    </>
  );
}

SeriesPostsForm.propTypes = {
  series: PropTypes.object.isRequired,
  zoneId: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default SeriesPostsForm;
